import { SelectComponents } from 'react-select/dist/declarations/src/components';
import React, { CSSProperties, useMemo } from 'react';
import {
  components,
  SelectComponentsConfig,
  StylesConfig,
  OnChangeValue,
  SingleValue,
  MultiValue,
  GroupBase,
  CSSObjectWithLabel,
  OptionProps,
  PlaceholderProps,
  ValueContainerProps,
  ControlProps,
  InputActionMeta,
} from 'react-select';
import { useSelector } from 'react-redux';

import MSelectValueContainer from './MSelectValueContainer/MSelectValueContainer';
import MSelectDropdownIndicator from './MSelectDropdownIndicator/MSelectDropdownIndicator';
import MSelectView from './MSelect.view';

import visibilityVariables from '../../styles/temporaryVariables/visibilityVariables.module.scss';
import colorsVariables from '../../styles/temporaryVariables/colorsVariables.module.scss';
import { AppState } from '../../models/app-store';
import { MReactSelectOption, MSelectChangeData, MSelectOption, MSelectOptionType } from './MSelect.model';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

type Props<Value, Id extends string, IsMulti extends boolean> = {
  className?: string;
  controlClassName?: string;
  heightOptions?: string;
  controlFontSize?: string;
  placeholder?: string;
  options: MSelectOption<Value>[] | null;
  isMulti?: IsMulti;
  value?: IsMulti extends true
    ? (MSelectOption<Value> | MSelectOption<Value>['id'])[]
    : MSelectOption<Value> | MSelectOption<Value>['id'] | null;
  customStylesSelect?: StylesConfig<MReactSelectOption, IsMulti>;
  name?: string;
  valid?: boolean;
  invalid?: boolean;
  hideSelectedOptions?: boolean;
  showSelected?: boolean;
  disabled?: boolean;
  disabledValueColor?: string;
  indicatorsContainerColor?: string;
  controlColor?: string;
  hideEffectSelection?: boolean;
  isClearable?: boolean;
  components?: Partial<SelectComponents<MReactSelectOption, IsMulti, GroupBase<MReactSelectOption>>>;
  isCreatable?: boolean;
  isLoading?: boolean;
  keepSearchOnSelect?: boolean;
  fixOverflow?: boolean;
  transparentBorder?: boolean;
  displayItemsCount?: boolean;
  selectAll?: boolean;
  menuZIndex?: number;
  stopPropagation?: boolean;
  dropDownInputColor?: CSSProperties;
  onCreateOption?: (inputValue: string) => void;
  onChange?: (value: OnChangeValue<MSelectChangeData<Value, Id>, IsMulti>) => void;
};

const MSelect = <Value, Id extends string = string, IsMulti extends boolean = false>(props: Props<Value, Id, IsMulti>): JSX.Element => {
  type ValueType = IsMulti extends true ? MultiValue<MReactSelectOption> : SingleValue<MReactSelectOption>;
  const [isShiftPressedState, setShiftPressedState] = React.useState(false);
  const [inputValueState, setInputValueState] = React.useState('');
  const { t } = useTranslation();

  const darkMode = useSelector((state: AppState) => state.ui.darkMode);

  const componentsObj = useMemo(() => {
    const cmps: SelectComponentsConfig<MReactSelectOption, IsMulti, GroupBase<MReactSelectOption>> = {
      Menu: components.Menu,
      Control: (items: React.PropsWithChildren<ControlProps<MReactSelectOption, IsMulti, GroupBase<MReactSelectOption>>>) =>
        components.Control({ ...items, className: classNames(items.className, props.controlClassName) }),
    };

    if (props.isMulti && !props.showSelected) {
      cmps.ValueContainer = (
        items: React.PropsWithChildren<ValueContainerProps<MReactSelectOption, IsMulti, GroupBase<MReactSelectOption>>>,
      ) => MSelectValueContainer({ ...items, displayItemsCount: !!props.displayItemsCount });
    }

    cmps.DropdownIndicator = MSelectDropdownIndicator;

    cmps.IndicatorSeparator = () => null;

    return cmps;
  }, [props.isMulti, props.showSelected]);

  const value: ValueType = useMemo(() => {
    if (props.isMulti) {
      const inputValue = props.value as (MSelectOption<Value> | MSelectOption<Value>['id'])[];

      return inputValue.reduce((acc: MReactSelectOption[], valueItem: MSelectOption<Value> | MSelectOption<Value>['id']) => {
        if (typeof valueItem === 'string' || typeof valueItem === 'number') {
          const option = props.options?.find((option: MSelectOption<Value>) => option.id === valueItem);
          const label = option?.label;

          if (!label) {
            return acc;
          }

          return [...acc, { label, value: option?.id ?? valueItem }];
        }

        return [...acc, { label: valueItem.label, value: valueItem.id }];
      }, []) as unknown as ValueType;
    }

    if (!props.value) {
      return null as unknown as ValueType;
    }

    const inputValue = props.value as MSelectOption<Value> | MSelectOption<Value>['id'];

    if (typeof inputValue === 'string' || typeof inputValue === 'number') {
      const option = props.options?.find((option: MSelectOption<Value>) => option.id === inputValue);
      const label = option?.label;

      if (!label) {
        if (props.isCreatable) {
          return { label: '', value: props.value } as unknown as ValueType;
        }

        return null as unknown as ValueType;
      }

      return { label, value: option?.id ?? inputValue } as unknown as ValueType;
    }

    return { label: inputValue.label, value: inputValue.id } as unknown as ValueType;
  }, [props.value, props.isMulti, props.options]);

  const options: MReactSelectOption[] =
    props.options?.map((option: MSelectOption<Value>) => {
      return { label: option.label, value: option.id, isDisabled: option.isDisabled };
    }) || [];

  if (props.selectAll && props.isMulti) {
    if (Array.isArray(props.value) && props.value.length === props.options?.length) {
      options.unshift({ label: t('common.unselectAll'), value: MSelectOptionType.UnselectAll, isDisabled: false });
    } else {
      options.unshift({ label: t('common.selectAll'), value: MSelectOptionType.SelectAll, isDisabled: false });
    }
  }

  const customStylesSelect: StylesConfig<MReactSelectOption, IsMulti> = useMemo(() => {
    const variables = getComputedStyle(document.documentElement);

    return {
      menu: (provided: CSSObjectWithLabel) => ({
        ...provided,
        boxShadow: 'none',
        zIndex: 1000,
        background: darkMode ? 'black' : 'white',
        color: darkMode ? colorsVariables.inputOptionsDark : colorsVariables.inputOptionsLight,
      }),
      menuList: (provided: CSSObjectWithLabel) => ({
        ...provided,
        position: 'absolute',
        width: '100%',
        height: props.heightOptions || 'fit-content',
        maxHeight: '300px',
        boxShadow: '2px 2px 10px -4px #4b4b4bc7',
        borderRadius: '8px',
        zIndex: props.menuZIndex || 0,
        backgroundColor: darkMode ? 'black' : 'white',
      }),
      option: (provided: CSSObjectWithLabel, state: OptionProps<MReactSelectOption, IsMulti, GroupBase<MReactSelectOption>>) => {
        let backgroundColor = darkMode ? 'black' : 'white';

        if (state.isSelected && !props.hideEffectSelection) {
          backgroundColor = darkMode ? colorsVariables.inputOptionsSelectedDark : colorsVariables.inputOptionsSelectedLight;
        }

        if (state.isFocused) {
          backgroundColor = darkMode ? colorsVariables.inputOptionsFocusedDark : colorsVariables.inputOptionsFocusedLight;
        }

        if (state.isSelected && state.isFocused) {
          backgroundColor = darkMode ? colorsVariables.inputOptionsSelectedDark : colorsVariables.inputOptionsSelectedLight;
        }

        return {
          ...provided,
          'display': 'flex',
          backgroundColor,
          ':active': {
            backgroundColor: darkMode ? colorsVariables.inputOptionsSelectedDark : colorsVariables.inputOptionsSelectedLight,
          },
        };
      },
      control: (provided: CSSObjectWithLabel) => ({
        ...provided,
        'minHeight': '32px',
        'borderWidth': '1px',
        'fontSize': props.controlFontSize || 'inherit',
        'background': variables.getPropertyValue('--input-text-background'),
        'borderStyle': props.transparentBorder ? 'none !important' : 'solid',
        'borderColor': props.invalid ? variables.getPropertyValue('--input-border-error') : variables.getPropertyValue('--input-border'),
        'borderRadius': '8px',
        'color': props.controlColor || darkMode ? colorsVariables.inputBorderDark : colorsVariables.inputBorderLight,
        ':hover': {
          borderColor: darkMode ? colorsVariables.inputBorderDark : colorsVariables.inputBorderLight,
        },
        ':focus-within': {
          borderWidth: '1px',
          opacity: visibilityVariables.high,
        },
      }),
      valueContainer: (provided: CSSObjectWithLabel) => ({
        ...provided,
        'minHeight': '32px',
        'padding': '0 6px',
        'borderStyle': 'none !important',
        'color': props.disabled
          ? props.disabledValueColor || '#8f8f8fc7'
          : darkMode
            ? colorsVariables.inputOptionsDark
            : colorsVariables.inputOptionsLight,
        ':focus-within': {
          color: darkMode ? colorsVariables.inputOptionsDark : colorsVariables.inputOptionsLight,
        },
      }),
      singleValue: (provided: CSSObjectWithLabel) => ({
        ...provided,
        'display': 'flex',
        'color': props.disabled
          ? props.disabledValueColor || props.dropDownInputColor?.color || '#8f8f8fc7'
          : props.dropDownInputColor?.color || darkMode
            ? colorsVariables.inputOptionsDark
            : colorsVariables.inputOptionsLight,
        ':focus-within': {
          color: darkMode ? colorsVariables.inputOptionsDark : colorsVariables.inputOptionsLight,
        },
      }),
      placeholder: (provided: CSSObjectWithLabel, state: PlaceholderProps<MReactSelectOption, IsMulti, GroupBase<MReactSelectOption>>) => ({
        ...provided,
        display: 'flex',
        color:
          props.controlColor ||
          (props.disabled
            ? props.disabledValueColor || '#8f8f8fc7'
            : state.isFocused
              ? darkMode
                ? colorsVariables.inputOptionsPlaceholderDark
                : colorsVariables.inputOptionsPlaceholderLight
              : darkMode
                ? colorsVariables.inputOptionsPlaceholderDark
                : colorsVariables.inputOptionsPlaceholderLight),
      }),
      multiValue: (provided: CSSObjectWithLabel) => {
        return {
          ...provided,
          fontSize: '15px',
          borderRadius: '4px',
          padding: '2px',
          color: darkMode ? colorsVariables.inputOptionsDark : colorsVariables.inputOptionsLight,
          backgroundColor: 'var(--input-select-box-background)',
        };
      },
      input: (provided: CSSObjectWithLabel) => ({
        ...provided,
        margin: '0px',
        color: darkMode ? colorsVariables.inputOptionsDark : colorsVariables.inputOptionsLight,
      }),
      indicatorsContainer: (provided: CSSObjectWithLabel) => ({
        ...provided,
        height: '32px',
        color: props.indicatorsContainerColor || (darkMode ? colorsVariables.inputBorderDark : colorsVariables.inputBorderLight),
        opacity: props.disabled ? visibilityVariables.low : visibilityVariables.medium,
      }),
      clearIndicator: (provided: CSSObjectWithLabel) => ({
        ...provided,
        cursor: 'pointer',
        opacity: visibilityVariables.medium,
      }),
    };
  }, [darkMode, props.disabled, props.invalid, props.controlFontSize, props.disabledValueColor, props.indicatorsContainerColor]);

  const promptChange = (event: OnChangeValue<MReactSelectOption, IsMulti>) => {
    if (!props.onChange) {
      return;
    }

    if (Array.isArray(event) && event.some((value: MReactSelectOption) => value.value === MSelectOptionType.SelectAll)) {
      props.onChange(props.options as unknown as OnChangeValue<MSelectChangeData<Value, Id>, IsMulti>);

      return;
    }

    if (Array.isArray(event) && event.some((value: MReactSelectOption) => value.value === MSelectOptionType.UnselectAll)) {
      props.onChange([] as unknown as OnChangeValue<MSelectChangeData<Value, Id>, IsMulti>);

      return;
    }

    if (props.isMulti) {
      const inputValue = event as ReadonlyArray<MReactSelectOption>;

      if (isShiftPressedState && inputValue.length === 2) {
        const startIndex = props.options?.findIndex((option: MSelectOption<Value>) => option.id === inputValue[0].value) || 0;
        const endIndex = props.options?.findIndex((option: MSelectOption<Value>) => option.id === inputValue[1].value) || 0;

        props.onChange(
          (startIndex <= endIndex + 1
            ? props.options?.slice(startIndex, endIndex + 1)
            : props.options?.slice(endIndex, startIndex + 1)) as unknown as OnChangeValue<MSelectChangeData<Value, Id>, IsMulti>,
        );

        return;
      }

      const selectedValues = inputValue.reduce((acc: MSelectOption<Value>[], valueItem: MReactSelectOption) => {
        const option = props.options?.find((option: MSelectOption<Value>) => option.id === valueItem.value);

        if (!option) {
          return acc;
        }

        return [...acc, option];
      }, []);

      props.onChange(selectedValues as unknown as OnChangeValue<MSelectChangeData<Value, Id>, IsMulti>);

      return;
    }

    if (event === null) {
      props.onChange(null as unknown as OnChangeValue<MSelectChangeData<Value, Id>, IsMulti>);

      return;
    }

    const inputValue = event as MReactSelectOption;
    const option = props.options?.find((option: MSelectOption<Value>) => option.id === inputValue.value);
    const value = props.value as MSelectOption<Value> | MSelectOption<Value>['id'];

    // If the value havn't changed, do nothing
    if ((typeof value === 'string' && props.value === option?.id) || (typeof value === 'object' && value?.id === option?.id)) {
      return;
    }

    if (!option) {
      props.onChange(null as unknown as OnChangeValue<MSelectChangeData<Value, Id>, IsMulti>);

      return;
    }

    props.onChange(option as unknown as OnChangeValue<MSelectChangeData<Value, Id>, IsMulti>);
  };

  const promptChangeFromCreate = (value: string) => {
    if (props.isMulti) {
      throw new Error('Create option is currently not supported for multi select');
    }

    if (!props.onCreateOption) {
      return;
    }

    props.onCreateOption(value);
  };

  const promptInputChange = (value: string, actionMeta: InputActionMeta) => {
    if (!props.keepSearchOnSelect) {
      return value;
    }

    if (actionMeta.action === 'input-change') {
      setInputValueState(() => value);

      return value;
    }

    return inputValueState;
  };

  return (
    <MSelectView
      invalid={props.invalid}
      className={props.className}
      placeholder={props.placeholder || ''}
      name={props.name}
      disabled={props.disabled}
      styles={props.customStylesSelect || customStylesSelect}
      isMulti={props.isMulti}
      value={value}
      closeMenuOnSelect={!props.isMulti}
      hideSelectedOptions={props.hideSelectedOptions}
      components={props.components || componentsObj}
      options={options}
      isDisabled={props.disabled}
      isClearable={props.isClearable}
      isCreatable={props.isCreatable}
      isLoading={props.isLoading}
      inputValue={props.keepSearchOnSelect ? inputValueState : undefined}
      fixOverflow={props.fixOverflow}
      onCreateOption={promptChangeFromCreate}
      onChange={promptChange}
      onInputChange={promptInputChange}
      shiftKeyDown={() => setShiftPressedState(() => true)}
      shiftKeyUp={() => setShiftPressedState(() => false)}
      stopPropagation={props.stopPropagation}
    ></MSelectView>
  );
};

MSelect.displayName = 'MSelect';
MSelect.defaultProps = {
  hideSelectedOptions: false,
};

export default MSelect;
